import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from './Dashboard';
import { MagnifyingGlass, Sparkle, Link as LinkIcon, Clock, ChartLine, Eye, Check } from '@phosphor-icons/react';
import { HTTP_STATUS } from '@/lib/constants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FunnelSpy = ({ user }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState('');
  const [monitorAdded, setMonitorAdded] = useState(false);
  const [addingMonitor, setAddingMonitor] = useState(false);

  // Auto-fill URL from ?url= query param (deep-link from Discovery)
  useEffect(() => {
    const prefilled = searchParams.get('url');
    if (prefilled) {
      setUrl(prefilled);
    }
  }, [searchParams]);

  const handleAnalyze = useCallback(async (e) => {
    e.preventDefault();
    if (!url.trim()) return;

    setLoading(true);
    setError('');
    setAnalysis(null);
    setMonitorAdded(false);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/funnels/analyze`,
        { url: url.trim() },
        { withCredentials: true }
      );
      setAnalysis(response.data);
      setUrl('');
    } catch {
      setError(t('spy.err_analyze'));
    } finally {
      setLoading(false);
    }
  }, [url, t]);

  const handleAddMonitor = useCallback(async () => {
    if (!analysis) return;
    setAddingMonitor(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/monitor`,
        { url: analysis.url, label: analysis.title },
        { withCredentials: true }
      );
      setMonitorAdded(true);
    } catch (err) {
      const status = err.response?.status;
      if (status === HTTP_STATUS.CONFLICT) {
        toast.error(t('spy.err_conflict'));
      } else if (status === 402) {
        toast.error(t('spy.err_limit'), {
          description: t('spy.err_limit_desc'),
          action: { label: t('spy.view_plans'), onClick: () => navigate('/pricing') }
        });
      } else {
        toast.error(t('spy.err_monitor'));
      }
    } finally {
      setAddingMonitor(false);
    }
  }, [analysis, navigate, t]);

  return (
    <DashboardLayout user={user} activePage="/funnel-spy">
      <div className="p-6" data-testid="funnel-spy-page">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-[#2563EB]/20 rounded-xl flex items-center justify-center">
              <MagnifyingGlass size={28} weight="bold" className="text-[#2563EB]" />
            </div>
            <h1 className="text-4xl font-[Outfit] font-bold tracking-tight">Funnel Spy</h1>
          </div>
          <p className="text-white/60">{t('spy.subtitle')}</p>
        </div>

        {/* Analyzer Form */}
        <div className="max-w-3xl mb-8">
          <form onSubmit={handleAnalyze} className="p-6 bg-[#121212] border border-white/10 rounded-xl">
            <label className="block text-sm font-medium text-white/80 mb-3">
              {t('spy.url_label')}
            </label>
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <LinkIcon size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" weight="bold" />
                <input
                  type="url"
                  data-testid="funnel-url-input"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://exemplo.com/produto"
                  required
                  className="w-full pl-12 pr-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:border-[#2563EB] transition-colors"
                />
              </div>
              <button
                type="submit"
                data-testid="analyze-btn"
                disabled={loading}
                className="px-6 py-3 bg-[#2563EB] hover:bg-[#3B82F6] rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    {t('spy.analyzing')}
                  </>
                ) : (
                  <>
                    <Sparkle size={20} weight="bold" />
                    {t('spy.analyze')}
                  </>
                )}
              </button>
            </div>
            {error && (
              <p className="mt-3 text-sm text-[#EF4444]" data-testid="error-message">{error}</p>
            )}
          </form>
        </div>

        {/* Analysis Results */}
        {analysis && (
          <div className="space-y-6" data-testid="analysis-results">
            {/* Overview */}
            <div className="p-6 bg-[#121212] border border-white/10 rounded-xl">
              <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
                <h2 className="text-2xl font-[Outfit] font-bold">{t('spy.overview')}</h2>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => navigate(`/auto-build?url=${encodeURIComponent(analysis.url)}&niche=${encodeURIComponent(analysis.meta_data?.h1_tags?.[0] || analysis.title || '')}&offer=${encodeURIComponent(analysis.meta_data?.description || '')}`)}
                    data-testid="clone-funnel-btn"
                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium bg-gradient-to-r from-[#A855F7] to-[#7C3AED] hover:opacity-90 transition shadow-lg shadow-purple-500/20"
                    title={t('spy.clone_tip')}
                  >
                    <Sparkle size={18} weight="fill" />
                    {t('spy.clone_funnel')}
                  </button>
                  <button
                    onClick={handleAddMonitor}
                    data-testid="add-to-monitor-btn"
                    disabled={addingMonitor || monitorAdded}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all disabled:cursor-not-allowed ${
                      monitorAdded
                        ? 'bg-[#10B981]/20 text-[#10B981]'
                        : 'bg-[#2563EB] hover:bg-[#3B82F6] text-white disabled:opacity-50'
                    }`}
                  >
                    {monitorAdded ? (
                      <>
                        <Check size={18} weight="bold" />
                        {t('spy.monitoring')}
                      </>
                    ) : (
                      <>
                        <Eye size={18} weight="bold" />
                        {addingMonitor ? t('spy.adding') : t('spy.add_monitor')}
                      </>
                    )}
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-white/60 mb-1 uppercase tracking-[0.2em]">{t('spy.col_title')}</p>
                  <p className="font-medium" data-testid="analysis-title">{analysis.title}</p>
                </div>
                <div>
                  <p className="text-sm text-white/60 mb-1 uppercase tracking-[0.2em]">URL</p>
                  <a
                    href={analysis.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#2563EB] hover:text-[#3B82F6] font-medium break-all"
                  >
                    {analysis.url}
                  </a>
                </div>
                <div>
                  <p className="text-sm text-white/60 mb-1 uppercase tracking-[0.2em]">{t('spy.col_date')}</p>
                  <div className="flex items-center gap-2">
                    <Clock size={16} weight="bold" className="text-white/60" />
                    <span className="font-medium">
                      {new Date(analysis.created_at).toLocaleString(i18n.language === 'pt' ? 'pt-BR' : 'en-US')}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Meta Data */}
            {analysis.meta_data && (
              <div className="p-6 bg-[#121212] border border-white/10 rounded-xl">
                <h2 className="text-2xl font-[Outfit] font-bold mb-4">{t('spy.extracted')}</h2>
                
                {analysis.meta_data.description && (
                  <div className="mb-4">
                    <p className="text-sm text-white/60 mb-2 uppercase tracking-[0.2em]">{t('spy.description')}</p>
                    <p className="text-white/80">{analysis.meta_data.description}</p>
                  </div>
                )}

                {analysis.meta_data.h1_tags?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm text-white/60 mb-2 uppercase tracking-[0.2em]">{t('spy.headlines')}</p>
                    <div className="space-y-2">
                      {analysis.meta_data.h1_tags.map((h1, idx) => (
                        <div key={`h1-${idx}-${h1.substring(0, 20)}`} className="p-3 bg-[#0A0A0A] rounded-lg">
                          <p className="text-white/80">{h1}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {analysis.meta_data.cta_buttons?.length > 0 && (
                  <div>
                    <p className="text-sm text-white/60 mb-2 uppercase tracking-[0.2em]">{t('spy.ctas')}</p>
                    <div className="flex flex-wrap gap-2">
                      {analysis.meta_data.cta_buttons.slice(0, 10).map((cta, idx) => (
                        <span
                          key={`cta-${idx}-${cta.substring(0, 15)}`}
                          className="px-3 py-1 bg-[#2563EB]/20 text-[#2563EB] rounded-full text-sm"
                        >
                          {cta}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* AI Insights */}
            <div className="p-6 bg-gradient-to-br from-[#2563EB]/10 to-[#3B82F6]/5 border border-[#2563EB]/30 rounded-xl">
              <div className="flex items-center gap-2 mb-4">
                <Sparkle size={24} weight="fill" className="text-[#2563EB]" />
                <h2 className="text-2xl font-[Outfit] font-bold">{t('spy.ai_insights')}</h2>
              </div>
              <div className="prose prose-invert max-w-none" data-testid="ai-insights">
                <p className="text-white/80 whitespace-pre-wrap leading-relaxed">
                  {analysis.ai_insights}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!analysis && !loading && (
          <div className="max-w-2xl mx-auto text-center py-16">
            <div className="w-20 h-20 bg-[#2563EB]/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <ChartLine size={40} weight="bold" className="text-[#2563EB]" />
            </div>
            <h3 className="text-2xl font-[Outfit] font-bold mb-3">{t('spy.empty_title')}</h3>
            <p className="text-white/60">
              {t('spy.empty_sub')}
            </p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default FunnelSpy;